import { useState, useEffect, useCallback } from 'react';
import type { Question, QuestionResponse, QuestionsData } from '@types';
import questionsData from '../data/questions.json';

const STORAGE_KEY = 'client_question_responses';

/**
 * Hook for managing client questionnaire responses
 */
export function useQuestions() {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [responses, setResponses] = useState<Map<string, string>>(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load questions from JSON file
    useEffect(() => {
        try {
            setIsLoading(true);
            const data = questionsData as QuestionsData;
            setQuestions(data.questions);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load questions');
            console.error('Error loading questions:', err);
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Load saved responses from localStorage
    useEffect(() => {
        const savedResponses = localStorage.getItem(STORAGE_KEY);
        if (savedResponses) {
            try {
                const parsed = JSON.parse(savedResponses);
                setResponses(new Map(Object.entries(parsed)));
            } catch (err) {
                console.error('Error loading saved responses:', err);
            }
        }
    }, []);

    // Save responses to localStorage whenever they change
    useEffect(() => {
        if (responses.size > 0) {
            const responsesObj = Object.fromEntries(responses.entries());
            localStorage.setItem(STORAGE_KEY, JSON.stringify(responsesObj));
        }
    }, [responses]);

    /**
     * Update a question's response
     */
    const updateResponse = useCallback((questionId: string, answer: string) => {
        setResponses(prev => {
            const updated = new Map(prev);
            if (answer.trim()) {
                updated.set(questionId, answer);
            } else {
                updated.delete(questionId);
            }
            return updated;
        });
    }, []);

    /**
     * Clear all responses
     */
    const clearResponses = useCallback(() => {
        setResponses(new Map());
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    /**
     * Get all responses as an array of QuestionResponse objects
     */
    const getResponses = useCallback((): QuestionResponse[] => {
        return Array.from(responses.entries()).map(([questionId, answer]) => ({
            questionId,
            answer
        }));
    }, [responses]);

    /**
     * Get a specific response by question ID
     */
    const getResponse = useCallback((questionId: string): string => {
        return responses.get(questionId) || '';
    }, [responses]);

    /**
     * Validate that all required questions have responses
     */
    const validateResponses = useCallback((): { isValid: boolean; missingRequired: string[] } => {
        const requiredQuestions = questions.filter(q => q.required);
        const missingRequired = requiredQuestions
            .filter(q => !responses.has(q.id) || !responses.get(q.id)?.trim())
            .map(q => q.id);

        return {
            isValid: missingRequired.length === 0,
            missingRequired
        };
    }, [questions, responses]);

    /**
     * Get formatted responses with question text for display/export
     */
    const getFormattedResponses = useCallback((locale: 'en' | 'ru' | 'kz') => {
        return questions
            .filter(q => responses.has(q.id))
            .map(question => ({
                question: question.question[locale],
                answer: responses.get(question.id) || '',
                questionId: question.id
            }));
    }, [questions, responses]);

    return {
        questions,
        responses,
        isLoading,
        error,
        updateResponse,
        clearResponses,
        getResponses,
        getResponse,
        validateResponses,
        getFormattedResponses
    };
}